import { Column, Row, ParsedData, ChartType, DataPoint } from './types';

const CHART_COLORS = [
  '#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed',
  '#0891b2', '#be185d', '#4f46e5', '#ea580c', '#16a34a',
];

export function getChartColors(): string[] {
  return CHART_COLORS;
}

export function isNumericValue(value: string): boolean {
  if (!value || value.trim() === '') return false;
  const cleaned = value.replace(/[R$%,.\s]/g, '').replace(',', '.');
  return !isNaN(Number(cleaned)) && cleaned !== '';
}

export function parseNumericValue(value: string): number {
  if (!value || value.trim() === '') return 0;
  let cleaned = value.replace(/[R$%\s]/g, '');

  if (cleaned.includes(',') && cleaned.includes('.')) {
    if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (cleaned.includes(',')) {
    const parts = cleaned.split(',');
    if (parts.length === 2 && parts[1].length <= 2) {
      cleaned = cleaned.replace(',', '.');
    } else {
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (cleaned.includes('.')) {
    const parts = cleaned.split('.');
    if (parts.length > 2 || parts[parts.length - 1].length === 3) {
      cleaned = cleaned.replace(/\./g, '');
    }
  }

  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

export function parseTableData(columns: Column[], rows: Row[]): ParsedData {
  const defaultResult: ParsedData = {
    chartData: [],
    labelKey: '',
    valueKeys: [],
    suggestedChart: 'bar',
    canChart: false,
    numericColumnIndexes: [],
    labelColumnIndex: null,
  };

  if (!columns || !rows || columns.length < 2 || rows.length === 0) {
    return defaultResult;
  }

  const numericColumns: number[] = [];
  const textColumns: number[] = [];

  columns.forEach((col, idx) => {
    let numericCount = 0;
    const sampleSize = Math.min(rows.length, 10);

    for (let i = 0; i < sampleSize; i++) {
      const val = rows[i]?.values?.[idx]?.str;
      if (val && isNumericValue(val)) {
        numericCount++;
      }
    }

    if (numericCount / sampleSize >= 0.7) {
      numericColumns.push(idx);
    } else {
      textColumns.push(idx);
    }
  });

  if (numericColumns.length === 0 || textColumns.length === 0) {
    return defaultResult;
  }

  const labelColIdx = textColumns[0];
  const labelKey = columns[labelColIdx].name;

  const valueKeys = numericColumns.map(idx => columns[idx].name);

  const chartData: DataPoint[] = rows.slice(0, 20).map((row) => {
    const point: DataPoint = {
      name: row.values[labelColIdx]?.str || '',
      value: 0,
    };

    numericColumns.forEach((colIdx) => {
      const colName = columns[colIdx].name;
      const numVal = parseNumericValue(row.values[colIdx]?.str || '0');
      point[colName] = numVal;
      if (colIdx === numericColumns[0]) {
        point.value = numVal;
      }
    });

    return point;
  });

  let suggestedChart: ChartType = 'bar';
  const uniqueLabels = new Set(chartData.map(d => d.name)).size;

  if (uniqueLabels <= 6 && numericColumns.length === 1) {
    suggestedChart = 'pie';
  } else if (rows.length > 10 && numericColumns.length === 1) {
    suggestedChart = 'area';
  } else if (numericColumns.length > 1) {
    suggestedChart = 'bar';
  }

  return {
    chartData,
    labelKey,
    valueKeys,
    suggestedChart,
    canChart: chartData.length > 0,
    numericColumnIndexes: numericColumns,
    labelColumnIndex: labelColIdx,
  };
}

export function formatChartValue(value: number): string {
  if (Math.abs(value) >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toLocaleString('pt-BR');
}
